import { MedplumClient } from "@medplum/core";
import {
  Bundle,
  BundleEntry,
  List,
  Patient,
  Practitioner,
  Reference,
  Resource,
} from "@medplum/fhirtypes";

import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { isMedplumIfMatchConflictError } from "@/utils/medplum-if-match-conflict";

import { PREGNANCY_RECORD_CODING_QUERY } from "../constants/record";
import { EDDDisplay, EDDParams, PregnancyRecord } from "../types";

import { buildPregnancyRecord, fetchPregnancyRecordList, fetchPregnancyRecords } from "./record";
import { buildPregnancyRecordBundleEntry } from "./record-bundle";
import { buildClosePregnancyRecordBundleEntry } from "./record-close-bundle";

// --- Create ------------------------------------------------------------------

/**
 * Returns the patient's current (active) pregnancy record, creating one via a
 * FHIR transaction bundle if none exists.
 *
 * When creating a new record, only `lastMenstrualPeriod` is forwarded to
 * `buildPregnancyRecordBundleEntry`. Use that function directly for full control
 * over ultrasound, custom EDD, and display options.
 *
 * @param medplum - Medplum client
 * @param patientId - The patient's FHIR resource ID
 * @param options.lastMenstrualPeriod - Optional LMP ISO date included in the new
 *   record transaction when no active record exists
 * @returns The current `PregnancyRecord`
 * @throws When the FHIR transaction response does not contain the created `List`
 *   resource, or when the resulting record cannot be built
 */
export async function findOrStartCurrentPregnancyRecord(
  medplum: MedplumClient,
  patientId: string,
  {
    eddDisplay = EDDDisplay.LMP,
    lastMenstrualPeriod,
    lastMenstrualPeriodReliability,
    ultrasoundDate,
    ultrasoundGestationalAgeWeeks,
    ultrasoundGestationalAgeDays,
    customEDDDate,
    derivedFrom,
  }: Omit<EDDParams, "closeExistingActiveRecord"> = {},
): Promise<PregnancyRecord> {
  const existingActiveRecord = await fetchPregnancyRecords(medplum, patientId, {
    status: "current",
  });

  if (existingActiveRecord.length > 0) {
    return existingActiveRecord[0];
  }

  const profile = medplum.getProfile() as Reference<Practitioner | Patient> | undefined;
  const entry = await buildPregnancyRecordBundleEntry(
    medplum,
    profile,
    joinReferenceString("Patient", patientId),
    {
      closeExistingActiveRecord: true,
      eddDisplay,
      lastMenstrualPeriod,
      lastMenstrualPeriodReliability,
      ultrasoundDate,
      ultrasoundGestationalAgeWeeks,
      ultrasoundGestationalAgeDays,
      customEDDDate,
      derivedFrom,
    },
  );

  const response = await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry,
  });

  const list = getResourcesFromBundle<List>("List", response as Bundle<List>)?.[0];

  if (!list) {
    throw new Error(`Failed to create pregnancy record: ${JSON.stringify(response)}`);
  }

  const pregnancyRecord = await buildPregnancyRecord(medplum, list);

  if (!pregnancyRecord) {
    throw new Error(`Failed to build pregnancy record: ${JSON.stringify(list)}`);
  }

  return pregnancyRecord;
}

// --- Update ------------------------------------------------------------------

/**
 * Closes the patient's active pregnancy record by retiring the `List` and resolving
 * the linked `Condition` in a single FHIR transaction bundle.
 *
 * When `list` is omitted, every `current` pregnancy `List` for the patient is retired, so a
 * duplicate current record cannot keep the pregnancy active after close-out. Returns `undefined`
 * without throwing when no active list can be found or the given list has no `id`.
 *
 * Each `List` write carries `If-Match`. When another write changes a list first, the lists are read
 * again and retired once more, skipping any that something else already closed.
 *
 * @param medplum - Medplum client
 * @param patientId - The patient's FHIR resource ID
 * @param options.list - Optional pre-fetched pregnancy `List` to close; when omitted,
 *   the current lists are fetched from the server
 * @param options.closeOutDate - Optional ISO date string for the condition's
 *   `abatementDateTime`; defaults to today at noon when omitted
 * @returns The FHIR `Bundle` transaction response, or `undefined` if no active
 *   pregnancy record exists
 */
export async function closePregnancyRecord(
  medplum: MedplumClient,
  patientId: string,
  { list, closeOutDate }: { list?: List; closeOutDate?: string },
): Promise<Bundle<Resource> | undefined> {
  const lists = list ? [list] : await fetchCurrentPregnancyRecordLists(medplum, patientId);
  const listsToClose = lists.filter((record) => record.id);
  if (!listsToClose.length) return;

  try {
    return await executeClosePregnancyRecords(medplum, listsToClose, closeOutDate);
  } catch (error) {
    if (!isMedplumIfMatchConflictError(error)) throw error;
  }

  const latestLists = await readLatestPregnancyRecordLists(medplum, patientId, list, listsToClose);
  const currentLists = latestLists.filter((record) => record.status === "current");
  if (!currentLists.length) return;

  return await executeClosePregnancyRecords(medplum, currentLists, closeOutDate);
}

async function fetchCurrentPregnancyRecordLists(
  medplum: MedplumClient,
  patientId: string,
): Promise<List[]> {
  const lists = await fetchPregnancyRecordList(medplum, joinReferenceString("Patient", patientId), {
    status: "current",
  });
  return lists ?? [];
}

/**
 * Reads the lists to close again after a version conflict, bypassing cached reads. A search also
 * finds a current list created since the first read.
 */
async function readLatestPregnancyRecordLists(
  medplum: MedplumClient,
  patientId: string,
  givenList: List | undefined,
  closedLists: List[],
): Promise<List[]> {
  if (givenList) {
    return await Promise.all(
      closedLists.map((record) => medplum.readResource("List", record.id!, { cache: "no-cache" })),
    );
  }

  medplum.invalidateSearches("List");
  return await fetchCurrentPregnancyRecordLists(medplum, patientId);
}

async function executeClosePregnancyRecords(
  medplum: MedplumClient,
  lists: List[],
  closeOutDate: string | undefined,
): Promise<Bundle<Resource>> {
  const entriesPerList = await Promise.all(
    lists.map((list) => buildClosePregnancyRecordBundleEntry(medplum, { list, closeOutDate })),
  );

  return await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: dropDuplicateRequests(entriesPerList.flat()),
  });
}

/**
 * Keeps the first entry for each request. Each list's close-out also cancels the patient's EDD
 * Observations, so closing several lists repeats those entries, and a transaction may not write the
 * same resource twice.
 */
function dropDuplicateRequests(entries: BundleEntry[]): BundleEntry[] {
  const seenRequests = new Set<string>();
  return entries.reduce<BundleEntry[]>((uniqueEntries, entry) => {
    const requestKey = `${entry.request?.method} ${entry.request?.url}`;
    if (!seenRequests.has(requestKey)) {
      seenRequests.add(requestKey);
      uniqueEntries.push(entry);
    }
    return uniqueEntries;
  }, []);
}

export type PregnancyRecordWriteResult =
  | { ok: true; data: Bundle<Resource> }
  | { ok: false; reason: "closed-after-source" | "version-conflict" };

/**
 * Writes the pregnancy record derived from a source resource, such as the QuestionnaireResponse
 * that triggered a bot. Use it for writes that can finish after the pregnancy was closed out.
 *
 * When the write would start a new record, it is skipped if a pregnancy record was retired after
 * `sourceLastUpdated`. The source was saved before that close-out, so it belongs to the closed
 * pregnancy. This check runs after the current-record read, so a close-out that retires the record
 * in between is still seen.
 *
 * An update to the current record carries `If-Match`. When another write changes the record first,
 * the entries are rebuilt from a fresh read and sent once more.
 *
 * @param medplum - Medplum client
 * @param authorReference - Optional author stored as `List.source` on a new record
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param options.sourceLastUpdated - `meta.lastUpdated` of the source resource; without it, the
 *   closed-after-source check is skipped
 * @returns `{ ok: true, data }` with the transaction response. `{ ok: false, reason }` with
 *   `"closed-after-source"` when the write was skipped, or `"version-conflict"` when the retry
 *   also found a newer version.
 */
export async function writePregnancyRecordFromSource(
  medplum: MedplumClient,
  authorReference: Reference<Practitioner | Patient> | undefined,
  patientReferenceString: string,
  {
    sourceLastUpdated,
    ...options
  }: Omit<EDDParams, "closeExistingActiveRecord"> & { sourceLastUpdated?: string },
): Promise<PregnancyRecordWriteResult> {
  for (let attempt = 0; attempt < 2; attempt++) {
    // A retry must see the version that won, not a cached search.
    if (attempt > 0) medplum.invalidateSearches("List");

    const entry = await buildPregnancyRecordBundleEntry(
      medplum,
      authorReference,
      patientReferenceString,
      options,
    );

    if (
      startsNewPregnancyRecord(entry) &&
      (await wasPregnancyRecordRetiredAfter(medplum, patientReferenceString, sourceLastUpdated))
    ) {
      return { ok: false, reason: "closed-after-source" };
    }

    try {
      const data = await medplum.executeBatch({
        resourceType: "Bundle",
        type: "transaction",
        entry,
      });
      return { ok: true, data };
    } catch (error) {
      if (!isMedplumIfMatchConflictError(error)) throw error;
    }
  }

  return { ok: false, reason: "version-conflict" };
}

function startsNewPregnancyRecord(entry: BundleEntry[]): boolean {
  return entry.some(
    (bundleEntry) =>
      bundleEntry.resource?.resourceType === "List" && bundleEntry.request?.method === "POST",
  );
}

async function wasPregnancyRecordRetiredAfter(
  medplum: MedplumClient,
  patientReferenceString: string,
  instant: string | undefined,
): Promise<boolean> {
  if (!instant) return false;

  const retiredList = await medplum.searchOne(
    "List",
    {
      subject: patientReferenceString,
      code: PREGNANCY_RECORD_CODING_QUERY,
      status: "retired",
      _lastUpdated: `gt${instant}`,
    },
    { cache: "no-cache" },
  );

  return Boolean(retiredList);
}
